import { useState, useEffect, useCallback, useMemo, Fragment } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Airplane,
  Plus,
  Trash,
  PencilSimple,
  ListBullets,
  Table as TableIcon,
  X,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAAND_NAMEN = [
  "Januari", "Februari", "Maart", "April", "Mei", "Juni",
  "Juli", "Augustus", "September", "Oktober", "November", "December",
];
const DAGNAMEN_KORT = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];

const VERLOF_TYPES = [
  { value: "vakantie", label: "Vakantie", color: "#3B82F6", bg: "#DBEAFE" },
  { value: "ziek", label: "Ziek", color: "#E53E3E", bg: "#FEE2E2" },
  { value: "feestdag", label: "Feestdag", color: "#9A3412", bg: "#FFEDD5" },
  { value: "onbetaald", label: "Onbetaald", color: "#F59E0B", bg: "#FEF3C7" },
  { value: "compensatie", label: "Compensatie", color: "#10B981", bg: "#D1FAE5" },
  { value: "anders", label: "Anders", color: "#6B7280", bg: "#F3F4F6" },
];

const KLEUREN_KEY = "verlof_kleuren_v1";
const DEFAULT_KLEUREN = {
  vakantie: "#DBEAFE",
  ziek: "#FEE2E2",
  feestdag: "#FFEDD5",
  onbetaald: "#FEF3C7",
  compensatie: "#D1FAE5",
  anders: "#F3F4F6",
  zaterdag: "#00CED1",
  zondag: "#FF6666",
};
const loadKleuren = () => {
  try {
    const v = JSON.parse(localStorage.getItem(KLEUREN_KEY) || "{}");
    return { ...DEFAULT_KLEUREN, ...v };
  } catch {
    return { ...DEFAULT_KLEUREN };
  }
};
// Donkere variant voor tekst — eenvoudige luminantie-check
const darkText = (hex) => {
  try {
    const h = hex.replace("#", "");
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    const l = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return l > 0.6 ? "#1F2937" : "#FFFFFF";
  } catch {
    return "#1F2937";
  }
};
const TYPE_INFO = Object.fromEntries(VERLOF_TYPES.map((t) => [t.value, t]));
const TYPE_LETTER = {
  vakantie: "V", ziek: "Z", feestdag: "F",
  onbetaald: "O", compensatie: "C", anders: "A",
};

// Belgische feestdagen (incl. Pasen-afhankelijke dagen)
const easterDate = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
};
const fmtIso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const beFeestdagen = (year) => {
  const easter = easterDate(year);
  return {
    [`${year}-01-01`]: "Nieuwjaar",
    [fmtIso(easter)]: "Pasen",
    [fmtIso(addDays(easter, 1))]: "Paasmaandag",
    [`${year}-05-01`]: "Dag v/d Arbeid",
    [fmtIso(addDays(easter, 39))]: "Hemelvaart",
    [fmtIso(addDays(easter, 49))]: "Pinksteren",
    [fmtIso(addDays(easter, 50))]: "Pinkstermaandag",
    [`${year}-07-21`]: "Nationale Feestdag",
    [`${year}-08-15`]: "O.L.V. Hemelvaart",
    [`${year}-11-01`]: "Allerheiligen",
    [`${year}-11-11`]: "Wapenstilstand",
    [`${year}-12-25`]: "Kerstmis",
  };
};

const num = (v) => Number(v ?? 0).toFixed(1).replace(".", ",");


const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("nl-BE", { day: "2-digit", month: "2-digit", year: "numeric" });
};

export default function Verlof() {
  const user = useAuth();
  const role = user?.role || "medewerker";
  const isAdmin = role === "admin" || role === "manager";

  const [view, setView] = useState("matrix"); // matrix | lijst
  const [medewerkers, setMedewerkers] = useState([]);
  const [filialen, setFilialen] = useState([]);
  const [verlof, setVerlof] = useState([]);

  const now = new Date();
  const [maand, setMaand] = useState(String(now.getMonth() + 1));
  const [jaar, setJaar] = useState(String(now.getFullYear()));
  const [matrix, setMatrix] = useState(null);
  const [filterMedewerker, setFilterMedewerker] = useState("all");
  const [filterFiliaal, setFilterFiliaal] = useState("all");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    medewerker_id: "",
    filiaal_id: "",
    datum_van: "",
    datum_tot: "",
    type: "vakantie",
    halve_dag: false,
    opmerking: "",
  });
  const [saving, setSaving] = useState(false);

  // Kleuren-instellingen
  const [kleuren, setKleuren] = useState(loadKleuren);
  const [kleurenOpen, setKleurenOpen] = useState(false);
  const persistKleuren = (next) => {
    setKleuren(next);
    localStorage.setItem(KLEUREN_KEY, JSON.stringify(next));
  };

  // Load
  useEffect(() => {
    axios.get(`${API}/medewerkers`).then((r) => setMedewerkers(r.data || []));
    axios.get(`${API}/filialen`).then((r) => setFilialen(r.data || []));
  }, []);

  // Eén filiaal? dan automatisch invullen
  const enigFiliaal = filialen.length === 1 ? filialen[0].id : "";

  const loadList = useCallback(async () => {
    try {
      const params = {};
      if (filterFiliaal !== "all") params.filiaal_id = filterFiliaal;
      const res = await axios.get(`${API}/verlof`, { params });
      setVerlof(res.data || []);
    } catch (e) {
      toast.error("Fout bij laden verlofgegevens");
    }
  }, [filterFiliaal]);

  const loadMatrix = useCallback(async () => {
    try {
      const params = { jaar: parseInt(jaar), maand: parseInt(maand) };
      if (filterFiliaal !== "all") params.filiaal_id = filterFiliaal;
      const res = await axios.get(`${API}/verlof/matrix`, { params });
      setMatrix(res.data);
    } catch (e) {
      toast.error("Fout bij laden matrix");
    }
  }, [jaar, maand, filterFiliaal]);

  const [bewerkModus, setBewerkModus] = useState(false);
  const [verlofWijzigingen, setVerlofWijzigingen] = useState({});
  const [verlofSaving, setVerlofSaving] = useState(false);

  useEffect(() => { setBewerkModus(false); setVerlofWijzigingen({}); }, [jaar, maand, view, filterFiliaal]);

  const zetVerlofCel = (medewerkerId, dagNr, waarde) =>
    setVerlofWijzigingen((prev) => ({ ...prev, [`${medewerkerId}|${dagNr}`]: waarde }));

  const aantalVerlofWijzigingen = Object.keys(verlofWijzigingen).length;

  const bewaarVerlofGrid = async () => {
    const items = Object.entries(verlofWijzigingen);
    if (items.length === 0) { setBewerkModus(false); return; }
    setVerlofSaving(true);
    try {
      for (const [key, waarde] of items) {
        const [medewerkerId, dagNr] = key.split("|");
        const datum = `${jaar}-${String(parseInt(maand)).padStart(2, "0")}-${String(dagNr).padStart(2, "0")}`;
        const [type_, half] = String(waarde || "").split(":");
        await axios.put(`${API}/verlof/dag`, {
          medewerker_id: medewerkerId,
          filiaal_id: (filterFiliaal !== "all" ? filterFiliaal : enigFiliaal) || null,
          datum,
          type: type_ || null,
          halve_dag: half === "half",
        });
      }
      setVerlofWijzigingen({});
      setBewerkModus(false);
      await Promise.all([loadMatrix(), loadList()]);
      toast.success(`${items.length} wijziging${items.length === 1 ? "" : "en"} opgeslagen`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verlof opslaan mislukt");
    } finally { setVerlofSaving(false); }
  };

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (view === "matrix") loadMatrix(); }, [view, loadMatrix]);

  // Filter visible medewerkers (role-based)
  const visibleMedewerkers = isAdmin
    ? medewerkers
    : medewerkers.filter((m) => m.id === user?.id);

  // ── Form ──
  const openNew = () => {
    setEditing(null);
    setForm({
      medewerker_id: isAdmin ? "" : (user?.id || ""),
      filiaal_id: enigFiliaal,
      datum_van: new Date().toISOString().slice(0, 10),
      datum_tot: new Date().toISOString().slice(0, 10),
      type: "vakantie",
      halve_dag: false,
      opmerking: "",
    });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      medewerker_id: item.medewerker_id,
      filiaal_id: item.filiaal_id || enigFiliaal,
      datum_van: item.datum_van,
      datum_tot: item.datum_tot,
      type: item.type,
      halve_dag: !!item.halve_dag,
      opmerking: item.opmerking || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.medewerker_id || !form.datum_van || !form.datum_tot) {
      toast.error("Vul alle velden in");
      return;
    }
    if (form.datum_van > form.datum_tot) {
      toast.error("Datum van moet vóór datum tot liggen");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/verlof/${editing.id}`, form);
        toast.success("Verlof bijgewerkt");
      } else {
        await axios.post(`${API}/verlof`, form);
        toast.success("Verlof toegevoegd");
      }
      setDialogOpen(false);
      loadList();
      if (view === "matrix") loadMatrix();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fout bij opslaan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Verlof verwijderen?")) return;
    try {
      await axios.delete(`${API}/verlof/${id}`);
      toast.success("Verwijderd");
      loadList();
      if (view === "matrix") loadMatrix();
    } catch (e) {
      toast.error("Fout bij verwijderen");
    }
  };

  const filteredVerlof = useMemo(() => {
    let arr = verlof;
    if (!isAdmin) arr = arr.filter((v) => v.medewerker_id === user?.id);
    if (filterMedewerker !== "all" && isAdmin) {
      arr = arr.filter((v) => v.medewerker_id === filterMedewerker);
    }
    return arr;
  }, [verlof, isAdmin, user, filterMedewerker]);

  const visibleMatrixRows = useMemo(() => {
    if (!matrix) return [];
    if (!isAdmin) return matrix.rijen.filter((r) => r.medewerker_id === user?.id);
    return matrix.rijen;
  }, [matrix, isAdmin, user]);

  // Saldo
  const [saldo, setSaldo] = useState(null);
  useEffect(() => {
    axios
      .get(`${API}/verlof/saldo`, { params: { jaar: parseInt(jaar) } })
      .then((r) => setSaldo(r.data))
      .catch(() => setSaldo(null));
  }, [jaar, verlof]);

  const visibleSaldo = useMemo(() => {
    if (!saldo) return [];
    if (!isAdmin) return saldo.rijen.filter((r) => r.medewerker_id === user?.id);
    return saldo.rijen;
  }, [saldo, isAdmin, user]);

  // Exports
  const exportExcel = async () => {
    try {
      const res = await axios.get(`${API}/verlof/export-excel`, {
        params: {
          jaar: parseInt(jaar),
          maand: parseInt(maand),
          ...(filterFiliaal !== "all" ? { filiaal_id: filterFiliaal } : {}),
        },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `verlof_${jaar}_${maand.padStart(2, "0")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Excel export mislukt");
    }
  };

  const exportPdf = async () => {
    try {
      const res = await axios.get(`${API}/verlof/export-pdf`, {
        params: {
          jaar: parseInt(jaar),
          maand: parseInt(maand),
          ...(filterFiliaal !== "all" ? { filiaal_id: filterFiliaal } : {}),
        },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `verlof_${jaar}_${maand.padStart(2, "0")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF export mislukt");
    }
  };

  return (
    <div className="space-y-5" data-testid="verlof-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1
            className="text-2xl font-black tracking-tight flex items-center gap-2"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            <Airplane size={24} weight="bold" className="text-[#002FA7]" />
            Verlof
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Beheer vakantie, ziekte- en feestdagen voor het hele team.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={exportExcel}
            data-testid="header-excel-btn"
            disabled={!matrix}
          >
            Excel
          </Button>
          <Button
            variant="outline"
            onClick={exportPdf}
            data-testid="header-pdf-btn"
            disabled={!matrix}
          >
            PDF
          </Button>
          <Button
            onClick={openNew}
            className="bg-[#002FA7] hover:bg-[#002280] text-white"
            data-testid="add-verlof-btn"
          >
            <Plus size={16} weight="bold" className="mr-1" />
            Verlof toevoegen
          </Button>
        </div>
      </div>

      {/* View toggle */}
      <div className="grid grid-cols-3 gap-1 p-1 bg-muted rounded-sm max-w-xl" data-testid="view-toggle">
        <button
          onClick={() => setView("matrix")}
          className={`flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider py-2.5 rounded-sm transition-colors ${
            view === "matrix" ? "bg-white text-[#002FA7] shadow-sm" : "text-muted-foreground"
          }`}
          data-testid="view-matrix"
        >
          <TableIcon size={14} weight="bold" /> Matrix
        </button>
        <button
          onClick={() => setView("lijst")}
          className={`flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider py-2.5 rounded-sm transition-colors ${
            view === "lijst" ? "bg-white text-[#002FA7] shadow-sm" : "text-muted-foreground"
          }`}
          data-testid="view-lijst"
        >
          <ListBullets size={14} weight="bold" /> Lijst
        </button>
        <button
          onClick={() => setView("saldo")}
          className={`flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider py-2.5 rounded-sm transition-colors ${
            view === "saldo" ? "bg-white text-[#002FA7] shadow-sm" : "text-muted-foreground"
          }`}
          data-testid="view-saldo"
        >
          Saldo
        </button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-2 text-[11px] items-center">
        {VERLOF_TYPES.map((t) => {
          const bg = kleuren[t.value] || t.bg;
          return (
            <div
              key={t.value}
              className="flex items-center gap-1.5 px-2 py-1 rounded-sm border border-border bg-white"
            >
              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: bg, border: `1px solid #999` }} />
              <span>{t.label} ({TYPE_LETTER[t.value]})</span>
            </div>
          );
        })}
        <Button
          size="sm"
          variant="outline"
          onClick={() => setKleurenOpen(true)}
          className="text-[11px] h-7"
          data-testid="kleuren-btn"
        >
          Kleuren aanpassen
        </Button>
      </div>

      {/* ═══ Matrix ═══ */}
      {view === "matrix" && (
        <div className="bg-white border border-border rounded-sm p-3 sm:p-5" data-testid="matrix-card">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
            <div>
              <Label className="text-xs font-semibold">Filiaal</Label>
              <Select value={filterFiliaal} onValueChange={setFilterFiliaal}>
                <SelectTrigger className="mt-1.5" data-testid="matrix-filiaal"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle filialen</SelectItem>
                  {filialen.map((f) => (<SelectItem key={f.id} value={f.id}>{f.naam}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Maand</Label>
              <Select value={maand} onValueChange={setMaand}>
                <SelectTrigger className="mt-1.5" data-testid="matrix-maand"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MAAND_NAMEN.map((n, i) => <SelectItem key={i} value={String(i + 1)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Jaar</Label>
              <Select value={jaar} onValueChange={setJaar}>
                <SelectTrigger className="mt-1.5" data-testid="matrix-jaar"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2 flex-wrap">
              {isAdmin && (bewerkModus ? (
                <>
                  <Button onClick={bewaarVerlofGrid} disabled={verlofSaving} className="text-xs bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="verlof-opslaan-btn">
                    {verlofSaving ? "Opslaan..." : `Opslaan (${aantalVerlofWijzigingen})`}
                  </Button>
                  <Button variant="outline" onClick={() => { setVerlofWijzigingen({}); setBewerkModus(false); }} disabled={verlofSaving} className="text-xs" data-testid="verlof-annuleren-btn">
                    Annuleren
                  </Button>
                </>
              ) : (
                <Button variant="outline" onClick={() => setBewerkModus(true)} className="text-xs" data-testid="verlof-bewerken-btn" disabled={!matrix}>
                  Verlof invullen
                </Button>
              ))}
              <Button variant="outline" onClick={exportExcel} className="flex-1 text-xs" data-testid="matrix-excel" disabled={!matrix}>
                Excel
              </Button>
              <Button variant="outline" onClick={exportPdf} className="flex-1 text-xs" data-testid="matrix-pdf" disabled={!matrix}>
                PDF
              </Button>
            </div>
          </div>

          {!matrix ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Laden...</div>
          ) : (() => {
            const feestdagen = beFeestdagen(parseInt(jaar));
            return (
            <>
            <p className="md:hidden text-[10px] text-muted-foreground mb-1 font-semibold">↔ Veeg zijwaarts om alle dagen te zien</p>
            <div className="bg-white border border-[#999] rounded-sm overflow-x-auto" data-testid="verlof-matrix-table">
              <table className="text-[11px] border-collapse w-full" style={{ minWidth: "900px" }}>
                <thead>
                  <tr>
                    <th
                      className="sticky left-0 z-10 bg-[#E0FFFF] px-2 py-1.5 text-left font-bold border border-[#999]"
                      style={{ width: "140px", minWidth: "140px" }}
                    >
                      Personeel
                    </th>
                    {Array.from({ length: matrix.aantal_dagen }).map((_, i) => {
                      const wd = matrix.weekdagen[i];
                      const dStr = `${matrix.jaar}-${String(matrix.maand).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`;
                      const isFeest = !!feestdagen[dStr];
                      let bg = "#E0FFFF";
                      let color;
                      if (wd === 6) { bg = kleuren.zondag; color = darkText(bg); }
                      else if (wd === 5) { bg = kleuren.zaterdag; color = darkText(bg); }
                      else if (isFeest) { bg = kleuren.feestdag; color = darkText(bg); }
                      return (
                        <th
                          key={i}
                          className="px-0.5 py-1 text-center font-bold min-w-[28px] border border-[#999]"
                          style={{ backgroundColor: bg, color }}
                          title={isFeest ? feestdagen[dStr] : ""}
                        >
                          {i + 1}
                        </th>
                      );
                    })}
                  </tr>
                  <tr>
                    <th className="sticky left-0 z-10 bg-[#E0FFFF] px-2 py-0.5 border border-[#999]"></th>
                    {Array.from({ length: matrix.aantal_dagen }).map((_, i) => {
                      const wd = matrix.weekdagen[i];
                      const dStr = `${matrix.jaar}-${String(matrix.maand).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`;
                      const isFeest = !!feestdagen[dStr];
                      let bg = "#E0FFFF";
                      let color;
                      if (wd === 6) { bg = kleuren.zondag; color = darkText(bg); }
                      else if (wd === 5) { bg = kleuren.zaterdag; color = darkText(bg); }
                      else if (isFeest) { bg = kleuren.feestdag; color = darkText(bg); }
                      return (
                        <th
                          key={`n-${i}`}
                          className="px-0.5 py-0.5 text-center text-[9px] font-medium border border-[#999]"
                          style={{ backgroundColor: bg, color }}
                        >
                          {DAGNAMEN_KORT[wd]}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {visibleMatrixRows.map((row) => (
                    <tr key={row.medewerker_id} data-testid={`matrix-row-${row.medewerker_id}`}>
                      <td
                        className="sticky left-0 z-10 bg-white px-2 py-1 font-medium text-[11px] border border-[#999] truncate"
                        style={{ width: "140px", maxWidth: "140px" }}
                        title={row.medewerker_naam}
                      >
                        {row.medewerker_naam}
                      </td>
                      {row.dagen.map((cell, di) => {
                        const wd = matrix.weekdagen[di];
                        const dStr = `${matrix.jaar}-${String(matrix.maand).padStart(2, "0")}-${String(di + 1).padStart(2, "0")}`;
                        const feestNaam = feestdagen[dStr];
                        const info = cell ? TYPE_INFO[cell.type] : null;
                        let cellStyle = undefined;
                        let cellBgClass = "bg-white";
                        let label = null;
                        let titleText = "";

                        // Zondag = altijd rood, alleen F mag erop verschijnen
                        if (wd === 6) {
                          cellStyle = { backgroundColor: kleuren.zondag, color: darkText(kleuren.zondag) };
                          cellBgClass = "";
                          if (feestNaam) {
                            label = "F";
                            titleText = feestNaam;
                          }
                        } else if (wd === 5) {
                          // Zaterdag — eveneens vaste kleur, F mag verschijnen
                          cellStyle = { backgroundColor: kleuren.zaterdag, color: darkText(kleuren.zaterdag) };
                          cellBgClass = "";
                          if (feestNaam) {
                            label = "F";
                            titleText = feestNaam;
                          } else if (info) {
                            // Op zaterdag mag je nog wel verlof tonen
                            cellStyle = { backgroundColor: kleuren[cell.type] || info.bg, color: darkText(kleuren[cell.type] || info.bg) };
                            label = cell.halve_dag ? "½" : TYPE_LETTER[cell.type];
                            titleText = `${info.label}${cell.halve_dag ? " (½)" : ""}`;
                          }
                        } else if (info && feestNaam) {
                          // Feestdag telt niet als verlofdag, maar we tonen dat de medewerker vrij is
                          const bg = kleuren.feestdag;
                          cellStyle = { backgroundColor: bg, color: darkText(bg) };
                          cellBgClass = "";
                          label = TYPE_LETTER[cell.type];
                          titleText = `${feestNaam} (feestdag — telt niet mee) · ${info.label}`;
                        } else if (info) {
                          const bg = kleuren[cell.type] || info.bg;
                          cellStyle = { backgroundColor: bg, color: darkText(bg) };
                          cellBgClass = "";
                          label = cell.halve_dag ? "½" : TYPE_LETTER[cell.type];
                          titleText = `${info.label}${cell.halve_dag ? " (½)" : ""}`;
                        } else if (feestNaam) {
                          cellStyle = { backgroundColor: kleuren.feestdag, color: darkText(kleuren.feestdag) };
                          cellBgClass = "";
                          label = "F";
                          titleText = feestNaam;
                        }
                        const dagNr = di + 1;
                        const celKey = `${row.medewerker_id}|${dagNr}`;
                        const inEdit = isAdmin && bewerkModus && wd < 5;
                        const huidigType = verlofWijzigingen[celKey] !== undefined
                          ? verlofWijzigingen[celKey]
                          : (cell ? `${cell.type}${cell.halve_dag ? ":half" : ""}` : "");
                        const gewijzigd = verlofWijzigingen[celKey] !== undefined;
                        return (
                          <td
                            key={di}
                            className={`relative px-0.5 py-1 text-center tabular-nums border border-[#999] ${cellBgClass}`}
                            style={cellStyle}
                            title={titleText}
                            data-testid={inEdit ? `verlof-cel-${celKey}` : undefined}
                          >
                            {inEdit ? (
                              <select
                                value={huidigType}
                                onChange={(e) => zetVerlofCel(row.medewerker_id, dagNr, e.target.value)}
                                style={{ color: "transparent" }}
                                className={`absolute inset-0 w-full h-full text-center text-[11px] font-bold bg-transparent border-0 outline-none cursor-pointer appearance-none ${gewijzigd ? "ring-2 ring-[#F9A825]" : ""}`}
                                data-testid={`verlof-select-${celKey}`}
                              >
                                <option value="">– leeg</option>
                                {VERLOF_TYPES.map((t) => (
                                  <Fragment key={t.value}>
                                    <option value={t.value}>{TYPE_LETTER[t.value]} — {t.label}</option>
                                    <option value={`${t.value}:half`}>½ — {t.label} (halve dag)</option>
                                  </Fragment>
                                ))}
                              </select>
                            ) : (
                              label && <span className="font-bold text-[11px]">{label}</span>
                            )}
                            {inEdit && (
                              <span className="font-bold text-[11px] pointer-events-none">
                                {huidigType
                                  ? (huidigType.endsWith(":half") ? "½" : TYPE_LETTER[huidigType])
                                  : ""}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {visibleMatrixRows.length === 0 && (
                    <tr><td colSpan={matrix.aantal_dagen + 1} className="px-3 py-8 text-center text-muted-foreground border border-[#999]">Geen medewerkers.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            </>
            );
          })()}
        </div>
      )}

      {/* ═══ Lijst ═══ */}
      {view === "lijst" && (
        <div className="bg-white border border-border rounded-sm p-3 sm:p-5" data-testid="lijst-card">
          {isAdmin && (
            <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
              <div>
                <Label className="text-xs font-semibold">Filter medewerker</Label>
                <Select value={filterMedewerker} onValueChange={setFilterMedewerker}>
                  <SelectTrigger className="mt-1.5" data-testid="lijst-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle medewerkers</SelectItem>
                    {medewerkers.map((m) => (<SelectItem key={m.id} value={m.id}>{m.voornaam} {m.achternaam}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Filter filiaal</Label>
                <Select value={filterFiliaal} onValueChange={setFilterFiliaal}>
                  <SelectTrigger className="mt-1.5" data-testid="lijst-filter-filiaal"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle filialen</SelectItem>
                    {filialen.map((f) => (<SelectItem key={f.id} value={f.id}>{f.naam}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {filteredVerlof.length === 0 ? (
            <div className="text-sm text-muted-foreground py-12 text-center">
              Nog geen verlof ingegeven.
            </div>
          ) : (
            <>
            <div className="mob-list" data-testid="verlof-mobiel">
              {filteredVerlof.map((v) => {
                const info = TYPE_INFO[v.type] || TYPE_INFO.anders;
                return (
                  <div key={v.id} className="mob-card" data-testid={`verlof-kaart-${v.id}`}>
                    <div className="mob-card-head">
                      <p className="mob-title">{v.medewerker_naam}</p>
                      <span className="px-2 py-0.5 rounded-sm text-[10px] font-semibold shrink-0" style={{ backgroundColor: info.bg, color: info.color }}>
                        {info.label}{v.halve_dag ? " (½)" : ""}
                      </span>
                    </div>
                    <div className="mob-row mt-1.5">
                      <span className="mob-label">Periode</span>
                      <span className="mob-value">{fmtDate(v.datum_van)} → {fmtDate(v.datum_tot)}</span>
                    </div>
                    {v.opmerking && <p className="text-xs text-muted-foreground mt-1">{v.opmerking}</p>}
                    <div className="flex justify-end gap-1.5 mt-2 pt-2 border-t border-border">
                      <button onClick={() => openEdit(v)} className="p-1.5 rounded-sm border border-border" data-testid={`mob-edit-${v.id}`}>
                        <PencilSimple size={15} weight="bold" />
                      </button>
                      <button onClick={() => handleDelete(v.id)} className="p-1.5 rounded-sm border border-border text-[#E53E3E]" data-testid={`mob-delete-${v.id}`}>
                        <Trash size={15} weight="bold" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="hidden md:block overflow-x-auto border border-border rounded-sm">
              <table className="w-full text-xs">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-muted-foreground">Medewerker</th>
                    <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-muted-foreground">Van</th>
                    <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-muted-foreground">Tot</th>
                    <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-muted-foreground">Type</th>
                    <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-muted-foreground">Filiaal</th>
                    <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-muted-foreground">Opmerking</th>
                    <th className="px-3 py-2 text-right font-bold uppercase tracking-wider text-muted-foreground w-24">Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVerlof.map((v) => {
                    const info = TYPE_INFO[v.type] || TYPE_INFO.anders;
                    return (
                      <tr key={v.id} className="border-t border-border hover:bg-muted/30" data-testid={`verlof-row-${v.id}`}>
                        <td className="px-3 py-2 font-semibold">{v.medewerker_naam}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{fmtDate(v.datum_van)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{fmtDate(v.datum_tot)}</td>
                        <td className="px-3 py-2">
                          <span
                            className="inline-block px-2 py-0.5 rounded-sm text-[11px] font-semibold"
                            style={{ backgroundColor: info.bg, color: info.color }}
                          >
                            {info.label}{v.halve_dag ? " (½)" : ""}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground" data-testid={`verlof-filiaal-${v.id}`}>
                          {v.filiaal_naam || "Alle filialen"}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{v.opmerking || "—"}</td>
                        <td className="px-3 py-2">
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => openEdit(v)}
                              className="p-1 rounded-sm hover:bg-muted text-muted-foreground hover:text-foreground"
                              data-testid={`edit-${v.id}`}
                              title="Bewerken"
                            >
                              <PencilSimple size={14} weight="bold" />
                            </button>
                            <button
                              onClick={() => handleDelete(v.id)}
                              className="p-1 rounded-sm hover:bg-muted text-muted-foreground hover:text-[#E53E3E]"
                              data-testid={`delete-${v.id}`}
                              title="Verwijderen"
                            >
                              <Trash size={14} weight="bold" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      )}

      {/* ═══ Saldo ═══ */}
      {view === "saldo" && (
        <div className="bg-white border border-border rounded-sm p-3 sm:p-5" data-testid="saldo-card">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div>
              <Label className="text-xs font-semibold">Jaar</Label>
              <Select value={jaar} onValueChange={setJaar}>
                <SelectTrigger className="mt-1.5" data-testid="saldo-jaar"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {!saldo ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Laden...</div>
          ) : (
            <>
            <div className="mob-list" data-testid="saldo-mobiel">
              {visibleSaldo.map((r) => {
                const negative = r.resterend < 0;
                const low = r.resterend > 0 && r.resterend < 5;
                return (
                  <div key={r.medewerker_id} className="mob-card" data-testid={`saldo-kaart-${r.medewerker_id}`}>
                    <div className="mob-card-head">
                      <p className="mob-title">{r.medewerker_naam}</p>
                      <span className={`px-2 py-0.5 rounded-sm text-xs font-black tabular-nums shrink-0 ${
                        negative ? "bg-[#FEE2E2] text-[#E53E3E]" : low ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#D1FAE5] text-[#065F46]"
                      }`}>
                        {num(r.resterend)} resterend
                      </span>
                    </div>
                    <div className="mob-row mt-1.5"><span className="mob-label">Recht / opgenomen</span><span className="mob-value">{num(r.verlof_recht)} / {num(r.opgenomen)}</span></div>
                    <div className="mob-row"><span className="mob-label">Ziek / feestdag</span><span className="mob-value">{num(r.per_type?.ziek)} / {num(r.per_type?.feestdag)}</span></div>
                    <div className="mob-row"><span className="mob-label">Onbetaald / comp.</span><span className="mob-value">{num(r.per_type?.onbetaald)} / {num(r.per_type?.compensatie)}</span></div>
                  </div>
                );
              })}
            </div>
            <div className="hidden md:block overflow-x-auto border border-[#999] rounded-sm">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-[#E0FFFF]">
                    <th className="px-3 py-2 text-left font-bold border border-[#999]">Medewerker</th>
                    <th className="px-3 py-2 text-right font-bold border border-[#999]">Recht (dagen)</th>
                    <th className="px-3 py-2 text-right font-bold border border-[#999]">Vakantie opgenomen</th>
                    <th className="px-3 py-2 text-right font-bold border border-[#999]">Resterend</th>
                    <th className="px-3 py-2 text-right font-bold border border-[#999]">Ziek</th>
                    <th className="px-3 py-2 text-right font-bold border border-[#999]">Feestdag</th>
                    <th className="px-3 py-2 text-right font-bold border border-[#999]">Onbet.</th>
                    <th className="px-3 py-2 text-right font-bold border border-[#999]">Comp.</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleSaldo.map((r) => {
                    const negative = r.resterend < 0;
                    const low = r.resterend > 0 && r.resterend < 5;
                    return (
                      <tr key={r.medewerker_id} data-testid={`saldo-row-${r.medewerker_id}`}>
                        <td className="px-3 py-1.5 font-semibold border border-[#999] truncate max-w-[180px]" title={r.medewerker_naam}>
                          {r.medewerker_naam}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums border border-[#999]">{r.verlof_recht.toFixed(1).replace(".", ",")}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums border border-[#999]">{r.opgenomen.toFixed(1).replace(".", ",")}</td>
                        <td className={`px-3 py-1.5 text-right tabular-nums font-bold border border-[#999] ${
                          negative ? "bg-[#FEE2E2] text-[#E53E3E]" : low ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#D1FAE5] text-[#065F46]"
                        }`}>
                          {r.resterend.toFixed(1).replace(".", ",")}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums border border-[#999] text-muted-foreground">{r.per_type.ziek.toFixed(1).replace(".", ",")}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums border border-[#999] text-muted-foreground">{r.per_type.feestdag.toFixed(1).replace(".", ",")}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums border border-[#999] text-muted-foreground">{r.per_type.onbetaald.toFixed(1).replace(".", ",")}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums border border-[#999] text-muted-foreground">{r.per_type.compensatie.toFixed(1).replace(".", ",")}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-[11px] text-muted-foreground p-3 border-t border-[#999]">
                Het verlofrecht per medewerker pas je aan via de Medewerkers-pagina (veld "Verlof recht (dagen)"). Dagen zijn werkdagen (Ma-Vr); zaterdagen, zondagen en Belgische feestdagen tellen nooit mee. Een halve dag telt als 0,5.
              </p>
            </div>
            </>
          )}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="verlof-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Verlof bewerken" : "Verlof toevoegen"}</DialogTitle>
            <DialogDescription>Geef de periode en het type verlof op.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            {isAdmin && (
              <div>
                <Label className="text-xs font-semibold">Medewerker</Label>
                <Select value={form.medewerker_id} onValueChange={(v) => setForm({ ...form, medewerker_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="form-medewerker"><SelectValue placeholder="Selecteer..." /></SelectTrigger>
                  <SelectContent>
                    {visibleMedewerkers.map((m) => (<SelectItem key={m.id} value={m.id}>{m.voornaam} {m.achternaam}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs font-semibold">Filiaal</Label>
              <Select
                value={form.filiaal_id || "_geen"}
                onValueChange={(v) => setForm({ ...form, filiaal_id: v === "_geen" ? "" : v })}
                disabled={filialen.length === 1}
              >
                <SelectTrigger className="mt-1.5" data-testid="form-filiaal">
                  <SelectValue placeholder="Selecteer filiaal..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_geen">Alle filialen</SelectItem>
                  {filialen.map((f) => (<SelectItem key={f.id} value={f.id}>{f.naam}</SelectItem>))}
                </SelectContent>
              </Select>
              {filialen.length === 1 && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  Automatisch ingevuld — er is maar één filiaal.
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Datum van</Label>
                <Input type="date" value={form.datum_van} onChange={(e) => setForm({ ...form, datum_van: e.target.value })} className="mt-1.5" data-testid="form-datum-van" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Datum tot</Label>
                <Input type="date" value={form.datum_tot} onChange={(e) => setForm({ ...form, datum_tot: e.target.value })} className="mt-1.5" data-testid="form-datum-tot" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VERLOF_TYPES.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 bg-[#F4F5F7] border border-border rounded-sm p-2.5">
              <input
                id="halve-dag" type="checkbox"
                checked={form.halve_dag}
                onChange={(e) => setForm({ ...form, halve_dag: e.target.checked })}
                className="w-4 h-4 accent-[#002FA7]"
                data-testid="form-halve-dag"
              />
              <label htmlFor="halve-dag" className="text-xs cursor-pointer">
                Halve dag (½)
              </label>
            </div>
            <div>
              <Label className="text-xs font-semibold">Opmerking (optioneel)</Label>
              <Input value={form.opmerking} onChange={(e) => setForm({ ...form, opmerking: e.target.value })} className="mt-1.5" data-testid="form-opmerking" placeholder="Bv. familievakantie" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="form-cancel">
              <X size={14} className="mr-1" /> Annuleren
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="form-save">
              {saving ? "Opslaan..." : editing ? "Bijwerken" : "Toevoegen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Kleuren-instellingen */}
      <Dialog open={kleurenOpen} onOpenChange={setKleurenOpen}>
        <DialogContent data-testid="kleuren-dialog">
          <DialogHeader>
            <DialogTitle>Kleuren aanpassen</DialogTitle>
            <DialogDescription>
              Kies de achtergrondkleur voor elk type verlof, zaterdag en zondag.
              Wijzigingen worden lokaal opgeslagen.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {[
              ...VERLOF_TYPES.map((t) => ({ key: t.value, label: t.label })),
              { key: "zaterdag", label: "Zaterdag" },
              { key: "zondag", label: "Zondag" },
            ].map(({ key, label }) => (
              <div
                key={key}
                className="flex items-center gap-2 border border-border rounded-sm p-2"
              >
                <input
                  type="color"
                  value={kleuren[key] || "#FFFFFF"}
                  onChange={(e) =>
                    persistKleuren({ ...kleuren, [key]: e.target.value })
                  }
                  className="w-9 h-9 rounded-sm cursor-pointer border border-border"
                  data-testid={`kleur-${key}`}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold">{label}</p>
                  <p className="text-[10px] text-muted-foreground font-mono">
                    {kleuren[key]}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => persistKleuren({ ...DEFAULT_KLEUREN })}
              data-testid="kleuren-reset"
            >
              Standaard herstellen
            </Button>
            <Button
              onClick={() => setKleurenOpen(false)}
              className="bg-[#002FA7] hover:bg-[#002280] text-white"
              data-testid="kleuren-sluiten"
            >
              Sluiten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
